/**
 * Remove internal reference markers the model sometimes parrots out of the
 * injected context into its prose — these must NEVER reach the doctor:
 *   - bracketed UUIDs (lab/order/record ids): "Lipid panel [17b57693-…]"
 *   - span/status refs: [S] [S1] [E2]
 *   - tool cites: [T:search_web]
 * A preceding space is eaten too, so "Lipid panel [uuid]" → "Lipid panel".
 * Used by every surface that renders/speaks an assistant answer (workspace,
 * bubble, voice read-along) so the treatment can't diverge.
 */
const UUID_RE =
  / ?\[[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\]/g;
const REF_RE = / ?\[(?:[SE]\d*|T:[\w-]+)\]/g;
// The model sometimes INVENTS a citation label instead of the defined
// [E<n>]/[S] markers — "[Transcript]", "[Note]", "[Chart]" — which then
// render as raw noise. These bracketed source-words never belong in a
// clinical answer, so strip them on every surface (incl. reloaded turns
// the prompt fix can't reach). Kept narrow (a known noise-word list) so
// legitimate bracketed prose is untouched.
const NOISE_CITE_RE =
  / ?\[(?:Transcripts?|Notes?|Records?|Charts?|Sources?|EHR|EMR|Summary|Visit|Encounters?|Context)\]/gi;
// The model sometimes MERGES markers and drops the brackets — "[S][E1]" comes
// out as a bare "SE1" that no citation renderer matches, so it leaks as raw
// text ("…headache SE1."). Strip the bare merged form. Kept to "SE<n>" only —
// bare "S1"/"E1" can be clinical (spinal levels, L5-S1), so those are left be.
const BARE_MERGED_RE = / ?\bSE\d+\b/g;

/**
 * Full strip — removes leaked ids AND the [S]/[E]/[T:] citation markers.
 * Use on surfaces that DON'T render citations as chips: spoken TTS text,
 * the voice read-along, the floating bubble. There a bracket is pure noise.
 */
export function stripAssistantMarkers(text: string): string {
  if (!text) return text;
  return text
    .replace(UUID_RE, '')
    .replace(REF_RE, '')
    .replace(NOISE_CITE_RE, '')
    .replace(BARE_MERGED_RE, '');
}

/**
 * Id-only strip — removes leaked raw UUIDs but KEEPS [S]/[E]/[T:] markers
 * so a citation renderer can turn them into hover-able superscripts. Use on
 * the workspace chat, which renders them via MarkdownProse citations.
 */
export function stripLeakedIds(text: string): string {
  if (!text) return text;
  return text
    .replace(UUID_RE, '')
    .replace(NOISE_CITE_RE, '')
    .replace(BARE_MERGED_RE, '');
}
